import React from 'react';
import { motion } from 'framer-motion';
import { useAppContext } from '../../context/AppContext';
import { Github, Mail, MapPin } from 'lucide-react';
import { Button } from '../ui/button';

const Contact = () => {
  const { personalInfo } = useAppContext();

  return (
    <section id="contact" className="py-20 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl font-bold mb-2 terminal-text">
            <span className="text-primary">{'>'}</span> Contact Me
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="card-hover p-6 rounded-lg bg-card/50"
          >
            <h3 className="text-xl font-bold mb-6 terminal-text">
              <span className="text-primary">{'>'}</span> Get In Touch
            </h3>
            <p className="mb-8">
              I'm always open to discussing new projects, creative ideas or opportunities to be part of your vision. 
              Feel free to reach out through my contact information below.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <div className="p-2 bg-primary/10 rounded-md mr-4">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Email</h4>
                  <p className="text-foreground/70">{personalInfo.email}</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="p-2 bg-primary/10 rounded-md mr-4">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium">Location</h4>
                  <p className="text-foreground/70">{personalInfo.location}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-4">Connect with me</h4>
              <div className="flex space-x-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full border-primary/50 hover:bg-primary/10"
                  onClick={() => window.open(personalInfo.socialLinks.github, "_blank")}
                >
                  <Github className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline" 
                  size="icon"
                  className="rounded-full border-primary/50 hover:bg-primary/10"
                  onClick={() => window.location.href = `mailto:${personalInfo.email}`}
                >
                  <Mail className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="static-terminal-border w-full"
          >
            <div className="terminal-window w-full">
              <div className="terminal-header">
                <div className="terminal-button terminal-button-red"></div>
                <div className="terminal-button terminal-button-yellow"></div>
                <div className="terminal-button terminal-button-green"></div>
                <div className="terminal-title">contact_info.sh</div>
              </div>
              <div className="terminal-body">
                <div className="terminal-output mb-4">
                  <span className="terminal-prompt">$</span>{' '}
                  <span className="terminal-command">cat contact_info.txt</span>
                </div>
                <div className="terminal-output space-y-3 mt-4">
                  <p>
                    <span className="text-primary font-bold">NAME: </span>
                    {personalInfo.name}
                  </p>
                  <p>
                    <span className="text-primary font-bold">EMAIL: </span>
                    <a href={`mailto:${personalInfo.email}`} className="underline hover:text-primary">
                      {personalInfo.email}
                    </a>
                  </p>
                  <p>
                    <span className="text-primary font-bold">LOCATION: </span>
                    {personalInfo.location}
                  </p>
                  <p>
                    <span className="text-primary font-bold">GITHUB: </span>
                    <a
                      href={personalInfo.socialLinks.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-primary"
                    >
                      {personalInfo.socialLinks.github}
                    </a>
                  </p>
                  <p className="mt-6 text-foreground/80 italic">
                    Feel free to reach out via email or connect on GitHub!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;